import { useEffect, useState } from "react";
import { venueApi } from "../../../api/resources";
import { Button, TextInput } from "../../../components/ui";

interface Props {
  value: string;
  onChange: (tz: string) => void;
  lat: number | null;
  lng: number | null;
  zones: string[];
}

function useLocalTime(tz: string) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(t);
  }, []);
  try {
    return tz ? new Intl.DateTimeFormat(undefined, { timeZone: tz, dateStyle: "medium", timeStyle: "short" }).format(now) : null;
  } catch {
    return null;
  }
}

export function TimezoneField({ value, onChange, lat, lng, zones }: Props) {
  const [suggested, setSuggested] = useState<string | null>(null);
  const [text, setText] = useState(value);
  const local = useLocalTime(value);

  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (lat === null || lng === null) return;
    const t = setTimeout(() => {
      venueApi.timezone(lat, lng).then((r) => setSuggested(r.timezone)).catch(() => setSuggested(null));
    }, 400);
    return () => clearTimeout(t);
  }, [lat, lng]);

  const commit = (v: string) => {
    setText(v);
    onChange(zones.includes(v) ? v : "");
  };

  return (
    <div className="space-y-3">
      {suggested && suggested !== value && (
        <div data-testid="venue-timezone-suggestion" className="flex flex-wrap items-center gap-3 rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm">
          <span className="text-zinc-400">Suggested for this location:</span>
          <span className="font-mono text-white">{suggested}</span>
          <Button type="button" variant="secondary" className="h-8 px-3 text-[11px]" onClick={() => commit(suggested)} data-testid="venue-timezone-use-suggested-button">
            Use this timezone
          </Button>
        </div>
      )}
      <TextInput list="iana-zones" value={text} onChange={(e) => commit(e.target.value)} placeholder="Search IANA timezone, e.g. America/Chicago"
        aria-label="Timezone" data-testid="venue-timezone-input" />
      <datalist id="iana-zones">
        {zones.map((z) => <option key={z} value={z} />)}
      </datalist>
      {value ? (
        <p data-testid="venue-timezone-local-time" className="text-xs text-emerald-400">Confirmed: {value} · local time now {local}</p>
      ) : (
        <p data-testid="venue-timezone-unconfirmed" className="text-xs text-warn">Choose and confirm a valid IANA timezone.</p>
      )}
    </div>
  );
}
